import { useEffect, useState } from "react";
import { Activity, Webhook, Coins, Mail, AlertTriangle } from "lucide-react";

import { api } from "@/lib/api";

/**
 * Compact System Status card for the Retailer dashboard.
 *
 * Renders 4 health pills (webhook, mint, email, activity volume) scoped to
 * whichever tenant the current session is acting as — admins get the global
 * view, retailers (real or impersonated) see only their own metrics. Pulls
 * `/api/system/status` which already does the role-aware filtering on the
 * server, so this component contains no role logic.
 */
export default function SystemStatusCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get("/system/status")
      .then((r) => {
        if (alive) setStatus(r.data);
      })
      .catch(() => {
        if (alive) setStatus(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 text-xs text-gray-500" data-testid="system-status-loading">
        Loading system status…
      </div>
    );
  }
  if (!status) return null;

  const lastWebhook = status?.woocommerce?.last_webhook_at;
  const webhook24h = status?.woocommerce?.webhook_count_24h ?? 0;
  const webhookHealthy = !!lastWebhook && webhook24h > 0;
  const lastMint = status?.last_mint?.minted_at;
  const lastEmail = status?.last_email?.certificate_email_sent_at;
  const chainOk = !!status?.chain?.ok;

  return (
    <section
      data-testid="system-status-card"
      className="bg-white border border-gray-200 rounded-xl p-5 mb-6"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#0055FF] inline-flex items-center gap-1.5">
            <Activity size={11} />
            System Status
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">
            Health of your Authera pipeline · last 24h activity
          </div>
        </div>
        {!chainOk && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700">
            <AlertTriangle size={11} />
            Chain unreachable
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Pill
          icon={Webhook}
          label="Webhook"
          ok={webhookHealthy}
          subtext={
            lastWebhook
              ? `Last: ${new Date(lastWebhook).toLocaleString()}`
              : "No events yet"
          }
          testid="status-pill-webhook"
        />
        <Pill
          icon={Coins}
          label="Last Mint"
          ok={!!lastMint}
          subtext={
            lastMint
              ? new Date(lastMint).toLocaleString()
              : "No mints yet"
          }
          testid="status-pill-mint"
        />
        <Pill
          icon={Mail}
          label="Last Email"
          ok={!!lastEmail}
          subtext={
            lastEmail
              ? new Date(lastEmail).toLocaleString()
              : "No emails yet"
          }
          testid="status-pill-email"
        />
        <Pill
          icon={Activity}
          label="24h Volume"
          ok={webhook24h > 0}
          subtext={`${webhook24h} ingest event${webhook24h === 1 ? "" : "s"}`}
          testid="status-pill-volume"
        />
      </div>
    </section>
  );
}

function Pill({ icon: Icon, label, ok, subtext, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex items-start gap-3 bg-gray-50/60 border border-gray-100 rounded-lg p-3"
    >
      <span
        className={`mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-md ${
          ok ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
        }`}
      >
        <Icon size={13} strokeWidth={2.4} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold tracking-wider uppercase text-gray-500">
          {label}
        </div>
        <div className="text-xs text-gray-700 truncate" title={subtext}>
          {subtext}
        </div>
      </div>
    </div>
  );
}
